import React, { useEffect, useState } from 'react';
import api from '../../api';
import AdminLayout from './AdminLayout';
import { Card } from '../ui/card';
import { format } from 'date-fns';

const statusColor = {
  searching: 'bg-muted text-foreground',
  assigned:  'bg-accent/15 text-primary',
  arriving:  'bg-indigo-100 text-indigo-800',
  started:   'bg-emerald-100 text-emerald-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const paymentColor = {
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-muted text-muted-foreground',
  refunded: 'bg-amber-100 text-amber-800',
  refund_failed: 'bg-red-100 text-red-800',
};

const TABS = ['all', 'searching', 'assigned', 'started', 'completed', 'cancelled'];

export default function AdminBookings() {
  const [status, setStatus] = useState('all');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = status === 'all' ? {} : { status };
        const { data } = await api.get('/api/admin/bookings', { params });
        setBookings(data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [status]);

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Bookings</h1>
          <p className="text-muted-foreground text-sm">All trips across the platform</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setStatus(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              status === t ? 'bg-primary-dark text-white' : 'bg-white text-foreground hover:bg-muted border border-border'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Loading…</div>
        ) : bookings.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">No bookings to show.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-background text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Booking</th>
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Route</th>
                <th className="px-4 py-3 font-semibold">Driver</th>
                <th className="px-4 py-3 font-semibold">Hours</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Payment</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-border">
                  <td className="px-4 py-3 font-mono text-xs">{b.id.slice(0, 8)}</td>
                  <td className="px-4 py-3">{b.user_phone || b.user_id?.slice(0, 6)}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <div>{b.pickup_location}</div>
                    <div className="text-xs text-muted-foreground">→ {b.drop_location}</div>
                  </td>
                  <td className="px-4 py-3">{b.driver_name || <span className="text-muted-foreground/70">—</span>}</td>
                  <td className="px-4 py-3">{b.hours}</td>
                  <td className="px-4 py-3 font-semibold">₹{b.amount}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${paymentColor[b.payment_status] || 'bg-muted text-foreground'}`}>
                      {b.payment_status || 'pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor[b.status]}`}>
                      {b.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {b.created_at ? format(new Date(b.created_at), 'MMM dd HH:mm') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </AdminLayout>
  );
}
